import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Icon } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { Section } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardToken, CardTokenRepository } from '~/repositories/CardTokenRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';

import { CardTokenProps } from './CardToken';

interface Params {
  id: string;
}

const ActionCard = styled.div<{ $type?: 'success' | 'warning' | 'danger' | 'default' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme, $type }) =>
    $type === 'success'
      ? theme.success.blendToBackground(50)
      : $type === 'warning'
        ? theme.warning.blendToBackground(50)
        : $type === 'danger'
          ? theme.danger.blendToBackground(50)
          : theme.secondary.blendToBackground(50)};
  border: 1px solid
    ${({ theme, $type }) =>
      $type === 'success'
        ? theme.success.blendToBackground(150)
        : $type === 'warning'
          ? theme.warning.blendToBackground(150)
          : $type === 'danger'
            ? theme.danger.blendToBackground(150)
            : theme.secondary.blendToBackground(100)};
`;

const ActionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionIconWrapper = styled.div<{ $type?: 'success' | 'warning' | 'danger' | 'default' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ theme, $type }) =>
    $type === 'success'
      ? theme.success.blendToBackground(100)
      : $type === 'warning'
        ? theme.warning.blendToBackground(100)
        : $type === 'danger'
          ? theme.danger.blendToBackground(100)
          : theme.secondary.blendToBackground(100)};

  svg {
    --icon-size: 20px;
    --icon-color: ${({ theme, $type }) =>
      $type === 'success'
        ? theme.success.toString()
        : $type === 'warning'
          ? theme.warning.toString()
          : $type === 'danger'
            ? theme.danger.toString()
            : theme.secondary.blendToBackground(700)};
  }
`;

const ActionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ActionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};
`;

const ActionDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.secondary.blendToBackground(700)};
`;

const ActionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const NoActionsText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  padding: 8px 0;
`;

export const PageCardTokenActions: React.FC = () => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<keyof Params>() as Params;
  const { cardToken: outletCardToken } = useOutletContext<CardTokenProps>();
  const { currentPlatform, isSandbox } = useSessionStore();

  const queryKey = ['card-token', id, currentPlatform?.id, isSandbox] as const;

  const { data: cardToken, error: cardTokenError } = useQuery({
    queryKey,
    queryFn: () => CardTokenRepository.get(id!),
    enabled: !!id && !outletCardToken,
    initialData: outletCardToken,
  });

  const activateMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Card Token ID is required');
      return CardTokenRepository.activate(id);
    },
    onSuccess: (response: CardToken) => {
      queryClient.setQueryData(queryKey, response);
      addSuccessNotification({ content: 'Card token activated successfully' });
    },
    onError: (error: any) => {
      addDangerNotification({ content: error?.message || 'Failed to activate card token' });
    },
  });

  const suspendMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Card Token ID is required');
      return CardTokenRepository.suspend(id);
    },
    onSuccess: (response: CardToken) => {
      queryClient.setQueryData(queryKey, response);
      addSuccessNotification({ content: 'Card token suspended successfully' });
    },
    onError: (error: any) => {
      addDangerNotification({ content: error?.message || 'Failed to suspend card token' });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Card Token ID is required');
      return CardTokenRepository.deactivate(id);
    },
    onSuccess: (response: CardToken) => {
      queryClient.setQueryData(queryKey, response);
      addSuccessNotification({ content: 'Card token deactivated successfully' });
    },
    onError: (error: any) => {
      addDangerNotification({ content: error?.message || 'Failed to deactivate card token' });
    },
  });

  useEffect(() => {
    if (cardTokenError) {
      navigate(ROUTE.CARD_TOKENS);
      addDangerNotification({
        content: (cardTokenError as any).message || 'Failed to load card token',
      });
    }
  }, [cardTokenError, navigate, addDangerNotification]);

  if (!cardToken) {
    return null;
  }

  const isActive = cardToken.status === 'active';
  // The API returns 'paused' for a suspended token (internal CardTokenStatusPaused maps to the
  // 'paused' external status); the /pause mutation writes that response straight into the cache.
  const isSuspended = cardToken.status === 'paused';
  const isDeactivated = cardToken.status === 'deactivated';
  const isActionPending = activateMutation.isPending || suspendMutation.isPending || deactivateMutation.isPending;

  return (
    <Inner p={0}>
      <Section headline="Token Status">
        <Inner px="var(--page-margin)" py={16}>
          <ActionsGrid>
            {isActive && (
              <ActionCard $type="success">
                <ActionInfo>
                  <ActionIconWrapper $type="success">
                    <Icon.CircleCheck />
                  </ActionIconWrapper>
                  <ActionText>
                    <ActionTitle>Token is Active</ActionTitle>
                    <ActionDescription>This token can be used for transactions.</ActionDescription>
                  </ActionText>
                </ActionInfo>
                <ButtonGroup>
                  <Button
                    variant="secondary"
                    icon={<Icon.Offline />}
                    onClick={() => suspendMutation.mutate()}
                    isLoading={suspendMutation.isPending}
                    isDisabled={isActionPending}
                  >
                    Suspend
                  </Button>
                  <Button
                    variant="secondary"
                    icon={<Icon.Cross />}
                    onClick={() => deactivateMutation.mutate()}
                    isLoading={deactivateMutation.isPending}
                    isDisabled={isActionPending}
                  >
                    Deactivate
                  </Button>
                </ButtonGroup>
              </ActionCard>
            )}

            {isSuspended && (
              <ActionCard $type="warning">
                <ActionInfo>
                  <ActionIconWrapper $type="warning">
                    <Icon.Offline />
                  </ActionIconWrapper>
                  <ActionText>
                    <ActionTitle>Token is Suspended</ActionTitle>
                    <ActionDescription>This token is temporarily disabled and cannot be used.</ActionDescription>
                  </ActionText>
                </ActionInfo>
                <ButtonGroup>
                  <Button
                    variant="primary"
                    icon={<Icon.Play />}
                    onClick={() => activateMutation.mutate()}
                    isLoading={activateMutation.isPending}
                    isDisabled={isActionPending}
                  >
                    Activate
                  </Button>
                  <Button
                    variant="secondary"
                    icon={<Icon.Cross />}
                    onClick={() => deactivateMutation.mutate()}
                    isLoading={deactivateMutation.isPending}
                    isDisabled={isActionPending}
                  >
                    Deactivate
                  </Button>
                </ButtonGroup>
              </ActionCard>
            )}

            {isDeactivated && (
              <ActionCard $type="danger">
                <ActionInfo>
                  <ActionIconWrapper $type="danger">
                    <Icon.Cross />
                  </ActionIconWrapper>
                  <ActionText>
                    <ActionTitle>Token is Deactivated</ActionTitle>
                    <ActionDescription>This token has been permanently deactivated.</ActionDescription>
                  </ActionText>
                </ActionInfo>
              </ActionCard>
            )}

            {!isActive && !isSuspended && !isDeactivated && (
              <NoActionsText>No actions available for this token status.</NoActionsText>
            )}
          </ActionsGrid>
        </Inner>
      </Section>
    </Inner>
  );
};
